import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext();

export { SubscriptionContext };

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [trialInfo, setTrialInfo] = useState(null);
  const [cancellationInfo, setCancellationInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, token } = useAuth();

  // Fetch subscription data when user changes
  useEffect(() => {
    console.log('=== SUBSCRIPTION CONTEXT DEBUG ===');
    console.log('User ID:', user?._id);
    console.log('User email:', user?.email);
    console.log('User object keys:', user ? Object.keys(user) : 'No user');
    console.log('Token present:', !!token);
    console.log('User object:', user);
    console.log('User loading state:', loading);
    
    console.log('=== CONDITION CHECK ===');
    console.log('user exists:', !!user);
    console.log('token exists:', !!token);
    console.log('user type:', typeof user);
    console.log('user value:', user);
    
    if (user && token) {
      console.log('User and token available, fetching subscription...');
      fetchSubscription();
    } else if (!user && token) {
      console.log('Token exists but no user yet, waiting...');
      // Don't set loading to false yet, wait for user to load
    } else {
      console.log('No user or token, setting subscription to null');
      setSubscription(null);
      setLoading(false);
    }
  }, [user, token]);

  const fetchSubscription = async () => {
    try {
      console.log('=== FETCHING SUBSCRIPTION ===');
      const response = await api.get('/subscription/status');
      console.log('Subscription response:', response.data);
      setSubscription(response.data.subscription);
      setTrialInfo(response.data.trialInfo);
      setCancellationInfo(response.data.cancellationInfo);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      console.error('Error response:', error.response);
      setSubscription(null);
      setTrialInfo(null);
      setCancellationInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const createSubscription = async (planType) => {
    try {
      console.log('=== CREATING SUBSCRIPTION ===');
      console.log('Plan type:', planType);
      console.log('User:', user);
      console.log('Token present:', !!token);
      
      const response = await api.post('/subscription/create', {
        planType
      });
      
      console.log('Create subscription response:', response.data);
      
      if (response.data.success) {
        await fetchSubscription();
        return { success: true, checkoutUrl: response.data.checkoutUrl };
      }
      
      return { success: false, error: response.data.message };
    } catch (error) {
      console.error('Create subscription error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to create subscription' 
      };
    }
  };

  const cancelSubscription = async () => {
    try {
      const response = await api.post('/subscription/cancel');
      
      if (response.data.success) {
        await fetchSubscription();
        return { success: true };
      }
      
      return { success: false, error: response.data.message };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to cancel subscription' 
      };
    }
  };

  const updateSubscription = async (planType) => {
    try {
      const response = await api.post('/subscription/update', {
        planType
      });
      
      if (response.data.success) {
        await fetchSubscription();
        return { success: true };
      }
      
      return { success: false, error: response.data.message };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to update subscription' 
      };
    }
  };



  const getPlanFeatures = (planType) => {
    const plans = {
      base: {
        name: 'Base Plan',
        price: '$9.99/month',
        features: [
          'Access to one therapist 24/7',
          'Unlimited chat sessions',
          'Basic progress tracking',
          'Email support'
        ]
      },
      advanced: {
        name: 'Advanced Plan',
        price: '$19.99/month',
        features: [
          'Access to all therapists',
          'Cross-referencing of chat history',
          'Advanced progress tracking',
          'Voice mode support',
          'Priority support',
          'Mood tracking with AI analysis'
        ]
      }
    };
    
    return plans[planType] || plans.base;
  };

  const hasAccessToTherapist = (therapistId) => {
    console.log('=== HAS ACCESS TO THERAPIST DEBUG ===');
    console.log('Therapist ID:', therapistId);
    console.log('Subscription:', subscription);
    console.log('Trial Info:', trialInfo);
    console.log('User:', user);
    
    if (!subscription && !trialInfo?.isOnTrial) {
      console.log('No subscription or trial, returning false');
      return false;
    }
    
    // Trial users have access to all therapists (like advanced plan)
    if (trialInfo?.isOnTrial) {
      console.log('Trial user, has access to all therapists');
      return true;
    }
    
    // Check both plan and planType fields for consistency
    const userPlan = subscription?.plan || subscription?.planType;
    console.log('User plan:', userPlan);
    console.log('Subscription plan:', subscription?.plan);
    console.log('Subscription planType:', subscription?.planType);
    
    // Base plan users can only access one therapist
    if (userPlan === 'base') {
      const hasAccess = user?.preferences?.preferredTherapist === therapistId;
      console.log('Base plan user, has access:', hasAccess);
      return hasAccess;
    }
    
    // Advanced plan users can access all therapists
    const hasAccess = userPlan === 'advanced';
    console.log('Advanced plan user, has access:', hasAccess);
    return hasAccess;
  };

  const isOnTrial = () => {
    return trialInfo?.isOnTrial || false;
  };

  const isTrialExpired = () => {
    return trialInfo?.isTrialExpired || false;
  };

  const getTrialDaysRemaining = () => {
    return trialInfo?.trialDaysRemaining || 0;
  };

  const value = {
    subscription,
    trialInfo,
    cancellationInfo,
    loading,
    createSubscription,
    cancelSubscription,
    updateSubscription,

    getPlanFeatures,
    hasAccessToTherapist,
    isOnTrial,
    isTrialExpired,
    getTrialDaysRemaining,
    refreshSubscription: fetchSubscription
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}; 